import {
  BoxProps,
  Button,
  ButtonProps,
  CheckboxProps,
  FlexProps,
  ModalBodyProps,
  ModalContentProps,
  ModalHeaderProps,
  ModalProps,
  SimpleGridProps,
  TextProps,
  useDisclosure,
} from "@chakra-ui/react";
import {
  BigSeatLayoutContainer,
  Seat,
  SeatLayoutContainer,
  SeatLayoutProps,
  SeatLayoutRenderer,
  SeatLayoutRendererWithIcon,
  SeatLayoutRendererWithIconBg,
  SeatLayoutWrapperProps,
  useSnackbar,
} from "@picovico/ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useClearSeatSelectionMutation,
  useSeatSelectionMutation,
} from "store/services/audi";
import {
  addBookedSeats,
  addHoldedSeats,
  addSeatSelectedTime,
  addSeats,
  addUserSelectedSeats,
  initiateBooking,
  removeSeats,
} from "store/slices";

import { useAppToast } from "@/hooks/useAppToast";
import { useOrderSession } from "@/hooks/useOrderSession";
import { catchErrorHandler } from "@picovico/utils";
import useWebSocket from "hooks/useWebSocket";
import { useRouter } from "next/router";
import { BiErrorCircle, BiFilterAlt } from "react-icons/bi";
import { useDispatch } from "react-redux";
import { getPageRoutes } from "routes/pageRoutes";
import { useAppSelector } from "store/hooks";
import { SelectedSeat } from "types/api";
import ChangeShowModal from "./Modals/ChangeShowModal";
import SeatTypeSelectionModal, {
  SeatTypeOption,
} from "./Modals/SeatTypeSelectionModal";
import TimesUpModal from "./Modals/TimesUpModal";

function normalizeSeatTypeOption(ticketType: unknown): SeatTypeOption | null {
  if (!ticketType) return null;
  if (typeof ticketType === "string") {
    return { value: ticketType, label: ticketType };
  }
  if (typeof ticketType === "object") {
    const { idx, name } = ticketType as { idx?: string; name?: string };
    if (name) return { value: idx || name, label: name };
  }
  return null;
}

function SeatSelector<T>({
  seatLayouts = [],
  screenPosition = "Top",
  showDetail,
  movieShowDetail,
  disableSelectingSeats,
  isLoading,
  variant = "default",
  screenSideBoxProps,
  screenSideType,
  screenTextProps,
  screenTopScreenSideBoxPropsSx,
  screenBottomScreenSideBoxPropsSx,
  seatWrapperFlexProps,
  seatButtonExtraProps,
  seatWrapperBoxProps = {
    overflow: "auto",
    p: "0.5rem",
    className: "seat-layout-box-wrapper",
  },
  showPanZoomPinchLayout,
  panZoomPinchProps,
  seatBoxWrapperProps,
  seatLabelTextProps,
  enableSeatTypeSelection = false,
  seatTypeButtonProps,
  seatTypeModalProps,
  seatTypeModalContentProps,
  seatTypeModalHeaderProps,
  seatTypeModalBodyProps,
  seatTypeModalDescriptionProps,
  seatTypeModalClearButtonProps,
  seatTypeModalEmptyStateTextProps,
  seatTypeModalGridProps,
  seatTypeOptionCardProps,
  seatTypeOptionLabelProps,
  seatTypeOptionCheckboxProps,
  seatTypeModalApplyButtonProps,
  ...props
}: Pick<
  SeatLayoutProps,
  | "seatLayouts"
  | "screenBottomScreenSideBoxPropsSx"
  | "screenTopScreenSideBoxPropsSx"
  | "seatButtonExtraProps"
  | "screenPosition"
  | "disableSelectingSeats"
  | "screenSideBoxProps"
  | "seatWrapperBoxProps"
  | "seatWrapperFlexProps"
  | "variant"
  | "screenSideType"
  | "screenTextProps"
  | "showPanZoomPinchLayout"
  | "panZoomPinchProps"
  | "seatBoxWrapperProps"
  | "seatLabelTextProps"
> & {
  SeatLayoutComponent?: React.FC<T>;
  showDetail: {
    cinemaHall?: string;
    screenName?: string;
    showIdx: string;
    audiIdx: string;
    audi_layout: "row" | "col";
    cinemaHallIdx: string;
    seat_selected_time: string | null;
    location: string;
    three_d_price?: number;
  };
  isLoading?: boolean;
  seatWrapperBoxProps?: BoxProps;
  enableSeatTypeSelection?: boolean;
  seatTypeButtonProps?: ButtonProps;
  seatTypeModalProps?: ModalProps;
  seatTypeModalContentProps?: ModalContentProps;
  seatTypeModalHeaderProps?: ModalHeaderProps;
  seatTypeModalBodyProps?: ModalBodyProps;
  seatTypeModalDescriptionProps?: TextProps;
  seatTypeModalClearButtonProps?: ButtonProps;
  seatTypeModalEmptyStateTextProps?: TextProps;
  seatTypeModalGridProps?: SimpleGridProps;
  seatTypeOptionCardProps?: FlexProps;
  seatTypeOptionLabelProps?: TextProps;
  seatTypeOptionCheckboxProps?: CheckboxProps;
  seatTypeModalApplyButtonProps?: ButtonProps;
} & Pick<
    SeatLayoutWrapperProps,
    | "showCloseButton"
    | "movieShowDetail"
    | "actionButton"
    | "actionButtonProps"
    | "showChangeShowButton"
    | "changeShowButtonProps"
  >) {
  const [time, setTime] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isChangeShowOpen,
    onOpen: onChangeShowOpen,
    onClose: onChangeShowClose,
  } = useDisclosure();
  const {
    isOpen: isSeatTypeModalOpen,
    onOpen: onSeatTypeModalOpen,
    onClose: onSeatTypeModalClose,
  } = useDisclosure();
  const [selectedSeatTypes, setSelectedSeatTypes] = useState<string[]>([]);
  const hasPromptedSeatTypeSelection = useRef(false);
  const [isRunning, setIsRunning] = useState(false);
  const { showIdx, cinemaHallIdx, audiIdx } = useMemo(
    () => ({
      showIdx: showDetail?.showIdx,
      cinemaHallIdx: showDetail?.cinemaHallIdx,
      audiIdx: showDetail?.audiIdx,
    }),
    [showDetail],
  );

  const { error: errorToast, toast } = useAppToast();

  const router = useRouter();
  const dispatch = useDispatch();
  const { company_info, seat_availability_config } = useAppSelector(
    (state) => state.config,
  );
  const { selectedSeats } = useAppSelector(
    (state) => state.showSeatList.currentSelected,
  );

  const { selectedSeatsIdx, total } = useMemo(() => {
    const seatsTotal = selectedSeats.reduce((acc, curr) => {
      return acc + curr.price;
    }, 0);
    const threeDTotal = showDetail?.three_d_price
      ? showDetail.three_d_price * selectedSeats.length
      : 0;
    return {
      selectedSeatsIdx: selectedSeats.map((s) => s.idx),
      total: seatsTotal + threeDTotal || 0,
    };
  }, [selectedSeats, showDetail?.three_d_price]);
  const { maxSeatSelectWeb } = useAppSelector((state) => state.cineSetting);
  const { isLoggedIn } = useAppSelector((state) => state.user);

  const availableSeatTypes = useMemo(() => {
    if (!enableSeatTypeSelection) return [];
    const optionsByValue = new Map<string, SeatTypeOption>();
    seatLayouts.forEach((row) => {
      row?.seats?.forEach((seat) => {
        const option = normalizeSeatTypeOption(seat?.ticketType);
        if (option) optionsByValue.set(option.value, option);
      });
    });
    return Array.from(optionsByValue.values());
  }, [seatLayouts, enableSeatTypeSelection]);

  const filteredSeatLayouts = useMemo(() => {
    if (!enableSeatTypeSelection || selectedSeatTypes.length === 0) {
      return seatLayouts;
    }
    return seatLayouts
      .map((row) => ({
        ...row,
        seats: (row?.seats || []).filter((seat) => {
          const option = normalizeSeatTypeOption(seat?.ticketType);
          return option ? selectedSeatTypes.includes(option.value) : false;
        }),
      }))
      .filter((row) => row.seats.length > 0);
  }, [seatLayouts, selectedSeatTypes, enableSeatTypeSelection]);

  useEffect(() => {
    if (
      enableSeatTypeSelection &&
      !isLoading &&
      seatLayouts.length > 0 &&
      !hasPromptedSeatTypeSelection.current
    ) {
      hasPromptedSeatTypeSelection.current = true;
      onSeatTypeModalOpen();
    }
  }, [enableSeatTypeSelection, isLoading, seatLayouts, onSeatTypeModalOpen]);

  const handleLogin = useCallback(() => {
    const currentUrl = router.asPath;
    router.push(getPageRoutes("login", {}, { next: currentUrl }));
  }, [router]);

  const [seatSubmit] = useSeatSelectionMutation();
  const [clearSeats] = useClearSeatSelectionMutation();
  const { clearOrder } = useOrderSession();

  const onMessage = useCallback(
    ({ data }: MessageEvent<any>) => {
      if (data?.data?.others)
        dispatch(addBookedSeats(data?.data?.others || []));

      if (data?.data?.on_hold)
        dispatch(addHoldedSeats(data?.data?.on_hold || []));

      if (data?.data?.user) {
        dispatch(addUserSelectedSeats(data?.data?.user || []));
      }

      // if no user is present, set time to 5 minutes
      if (data?.data?.user.length === 0) {
        setTime(300000);
        setIsRunning(false);
      }
      // if ttl is present, set time to ttl
      else if (data?.data?.ttl > 0) {
        setTime(data.data.ttl * 1000);
        setIsRunning(true);
      }
    },
    [dispatch],
  );

  const {
    socket,
    isSocketClosed,
    socketRequestInitiated,
    setupSocket,
    isSocketReady,
  } = useWebSocket({
    onMessage,
  });

  const snackbar = useSnackbar();

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (selectedSeats.length === 0) {
      setIsRunning(false);
      setTime(300000);
    } else if (isRunning && time > 0) {
      intervalId = setInterval(() => {
        setTime((prevTime) => {
          const newTime = prevTime - 1000;
          if (newTime <= 0) {
            setIsRunning(false);
            selectedSeatsIdx.map((selected) => {
              const selectedSeat = selectedSeats.find(
                (x) => x.idx === selected,
              ) as SelectedSeat;
              dispatch(
                removeSeats({
                  movieShow: showDetail.showIdx,
                  seat: selectedSeat,
                }),
              );
            });
            clearSeats({
              movie_show: router.query.showIdx,
            });
            clearOrder();
            onOpen();
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    // if time is not running and selected seats are present and socket is closed
    else if (
      showDetail &&
      showDetail.seat_selected_time &&
      !isRunning &&
      selectedSeats.length > 0 &&
      isSocketClosed &&
      !isSocketReady
    ) {
      const now = new Date();
      const seatSelectedTime = new Date(showDetail.seat_selected_time);
      const diff = now.getTime() - seatSelectedTime.getTime();
      if (diff < 300000) {
        setTime(Math.max(0, 300000 - diff));
        setIsRunning(true);
      }
    } else {
      setTime(300000);
      setIsRunning(false);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    //eslint-disable-next-line
  }, [isRunning]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearOrder();
      router.query.showIdx &&
        clearSeats({
          movie_show: router.query.showIdx || showDetail.showIdx,
        });
    };

    const handleRouteChange = (url: string) => {
      const currentShowBookingPrefix = `/movie/${router.query.movieIdx}/booking/${router.query.showIdx}`;
      if (!url.startsWith(currentShowBookingPrefix)) {
        clearOrder();
        clearSeats({
          movie_show: router.query.showIdx || showDetail.showIdx,
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router, showDetail.showIdx, clearSeats, clearOrder]);

  useEffect(() => {
    if (socket) {
      if (socket?.readyState === socket?.CLOSED) {
        snackbar({
          text: "Could not connect to server. Please try again later.",
          icon: BiErrorCircle,
          actionButton: (
            <Button variant="link" onClick={() => window?.location.reload()}>
              Refresh
            </Button>
          ),
          iconProps: {
            colorScheme: "danger",
            "aria-label": "error",
          },
        });
      } else if (socket?.readyState === socket?.CONNECTING) {
        snackbar({
          open: false,
        });
      }
    }
  }, [snackbar, socket, socket?.readyState]);

  useEffect(() => {
    if (showIdx) setupSocket(`/selected/${showIdx}/`);
  }, [showIdx, setupSocket]);

  useEffect(() => {
    dispatch(
      initiateBooking({
        movieShow: showIdx,
        movieHall: cinemaHallIdx,
        auditorium: audiIdx,
      }),
    );
  }, [audiIdx, cinemaHallIdx, showIdx, dispatch]);

  const LayoutVariants = {
    default: SeatLayoutContainer,
    bigMovies: BigSeatLayoutContainer,
  };

  const SeatRendererByLayoutType = {
    BOX: SeatLayoutRenderer,
    ICON: SeatLayoutRendererWithIcon,
    ICON_BG: SeatLayoutRendererWithIconBg,
  };

  const SeatRenderer = variant
    ? LayoutVariants[variant as keyof typeof LayoutVariants]
    : SeatLayoutContainer;
  return (
    <>
      <SeatRenderer
        {...props}
        onChangeShow={onChangeShowOpen}
        headerExtraActions={
          enableSeatTypeSelection &&
          !isLoading &&
          seatLayouts.length > 0 && (
            <Button
              size={{ base: "xs", md: "sm" }}
              colorScheme="primary"
              variant="outline"
              color="white"
              borderColor="white"
              leftIcon={<BiFilterAlt />}
              onClick={onSeatTypeModalOpen}
              {...seatTypeButtonProps}
            >
              {selectedSeatTypes.length > 0
                ? `Seat Type (${selectedSeatTypes.length})`
                : "Filter Seat Type"}
            </Button>
          )
        }
        seatLoading={isLoading}
        seatAvailabilityConfig={seat_availability_config}
        movieShowDetail={movieShowDetail}
        screenDetail={{
          title: showDetail?.screenName ? `> ${showDetail?.screenName}` : "",
          screenPosition: 0,
          ...showDetail,
        }}
        time={!isLoggedIn ? undefined : time}
        totalAmount={total}
        isSeatSelected={selectedSeatsIdx.length > 0}
        seatLayoutRenderer={React.createElement(
          SeatRendererByLayoutType[
            company_info?.seat_layout as keyof typeof SeatRendererByLayoutType
          ] || SeatLayoutRenderer,
          {
            screenSideBoxProps,
            screenTextProps,
            screenTopScreenSideBoxPropsSx,
            screenBottomScreenSideBoxPropsSx,
            seatButtonExtraProps,
            seatLayouts: filteredSeatLayouts,
            audiLayout: showDetail.audi_layout,
            selectedSeats: selectedSeatsIdx,
            screenSideType,
            showPanZoomPinchLayout,
            panZoomPinchProps,
            seatBoxWrapperProps,
            seatLabelTextProps,
            // disableSelectingSeats: disableSelectingSeats || !isSocketReady,
            disableSelectingSeats:
              disableSelectingSeats ||
              (!socketRequestInitiated && !isSocketClosed),
            seatWrapperFlexProps: seatWrapperFlexProps,
            ...seatWrapperBoxProps,
            onSeatSelect: async (seat: Seat) => {
              if (!isLoggedIn) {
                handleLogin();
                return;
              }

              const isSelectingSameSeatAgain = selectedSeatsIdx.includes(
                seat.seatIdx,
              );
              const toastId = toast({
                status: "loading",
                description: isSelectingSameSeatAgain
                  ? "Removing seat..."
                  : "Adding seat...",
                duration: null,
                isClosable: false,
              });

              if (!showDetail.seat_selected_time) {
                dispatch(addSeatSelectedTime(new Date().toISOString()));
                setIsRunning(true);
              }

              if (
                maxSeatSelectWeb &&
                selectedSeatsIdx.length >= maxSeatSelectWeb &&
                !isSelectingSameSeatAgain
              ) {
                toast.close(toastId);
                errorToast(`You can only select ${maxSeatSelectWeb} seats.`);

                return;
              }

              const seatData = {
                idx: seat.seatIdx,
                price: seat.price,
              };

              try {
                const res = await seatSubmit({
                  show_idx: showDetail.showIdx,
                  seat_idx: seat.seatIdx,
                  status: !isSelectingSameSeatAgain,
                }).unwrap();

                if (res?.status_code === 200) {
                  if (isSelectingSameSeatAgain) {
                    dispatch(
                      removeSeats({
                        movieShow: showDetail.showIdx,
                        seat: seatData,
                      }),
                    );
                  } else {
                    dispatch(
                      addSeats({
                        movieShow: showDetail.showIdx,
                        seat: seatData,
                      }),
                    );
                    setIsRunning(true);
                  }

                  toast.update(toastId, {
                    status: "success",
                    description: isSelectingSameSeatAgain
                      ? "Seat removed successfully"
                      : "Seat added successfully",
                    duration: 3000,
                    isClosable: true,
                  });
                }
              } catch (error: any) {
                toast.close(toastId);
                catchErrorHandler(error, errorToast);
              }
            },
            screenPosition: screenPosition,
          },
        )}
        onClose={() => {
          if (!isLoggedIn) {
            handleLogin();
            return;
          }

          const confirmExit = window.confirm(
            "Are you sure you want to leave? Your booking may be lost.",
          );
          if (confirmExit) {
            clearSeats({
              movie_show: router.query.showIdx,
            });
            router.back();
          }
        }}
      />
      <TimesUpModal isOpen={isOpen} onClose={onClose} />
      {isChangeShowOpen && (
        <ChangeShowModal
          isOpen={isChangeShowOpen}
          onClose={onChangeShowClose}
          currentMovieIdx={router.query.movieIdx as string}
        />
      )}
      {enableSeatTypeSelection && (
        <SeatTypeSelectionModal
          isOpen={isSeatTypeModalOpen}
          onClose={onSeatTypeModalClose}
          seatTypes={availableSeatTypes}
          appliedSeatTypes={selectedSeatTypes}
          onApply={setSelectedSeatTypes}
          modalProps={seatTypeModalProps}
          modalContentProps={seatTypeModalContentProps}
          modalHeaderProps={seatTypeModalHeaderProps}
          modalBodyProps={seatTypeModalBodyProps}
          modalDescriptionProps={seatTypeModalDescriptionProps}
          modalClearButtonProps={seatTypeModalClearButtonProps}
          modalEmptyStateTextProps={seatTypeModalEmptyStateTextProps}
          modalGridProps={seatTypeModalGridProps}
          optionCardProps={seatTypeOptionCardProps}
          optionLabelProps={seatTypeOptionLabelProps}
          optionCheckboxProps={seatTypeOptionCheckboxProps}
          modalApplyButtonProps={seatTypeModalApplyButtonProps}
        />
      )}
    </>
  );
}

export default SeatSelector;
